<template>
  <div class="creator-packages-section">
    <Loader v-if="isLoadingPackages" />

    <template v-else>
      <div class="creator-packages-section__header">
        <h2 class="global-h1 creator-packages-section__title">Custom Packages</h2>

        <LinkWithIcon class="creator-packages-section__how-does-it-work-link" isDottedUnderline @click="domRefHowDoesItWorkModal?.openModal">
          <template #icon><IconEmbedded name="info_2-5" :size="17" /></template>
          <span>How does it work</span>
        </LinkWithIcon>

        <HowDoesItWorkModal ref="domRefHowDoesItWorkModal" />

        <EditPackageModal ref="domRefEditPackageModal" @save="loadAllPackages" />
      </div>

      <NoteWithIcon v-if="props.isEditProfileMode" class="creator-packages-section__explanation-info-section" color="blue">
        <template #icon><IconEmbedded name="info-simple_4" :size="25" /></template>
        <template #text>
          <div>
            <h5 class="global-h5" style="margin-bottom: 3px">Work directly with destinations via custom collab packages</h5>
            Pricing and deliverables can be negotiated as a work agreement is finalized.
            <div style="margin-top: 7px">
              Shrpa Creator Visits are different and covered by these
              <a class="global-a global-a--orange" href="https://explore.shrpa.com/creator-payment-policy/" target="_blank">Payment Terms</a>&nbsp;<IconEmbedded
                class="test-link__outer-icon"
                name="external-link_2-5"
                :size="15"
                color="rgba(0, 0, 0, 0.3)"
                style="position: relative; top: 2px; left: -2px"
              />
            </div>
          </div>
        </template>
      </NoteWithIcon>

      <ul v-if="packagesList.length" class="creator-packages-section__tiles-list">
        <PackageSnippet
          class="creator-packages-section__tile"
          v-for="packageObj in packagesList"
          :key="packageObj.id"
          :packageObj="packageObj"
          :isEditMode="props.isEditProfileMode"
          :creatorProfileId="props.creatorId"
          @edit="
            packageId =>
              domRefEditPackageModal.openModal(
                props.creatorId,
                packagesList.find(p => p.id === packageId)
              )
          "
          @delete="packageId => (packagesList = packagesList.filter(p => p.id !== packageId))"
          @clickGetStarted="
            packageId => {
              if (!props.isCustomerAdmin) {
                selectedPackageId = packageId;
                domRefGetStartedModal?.openModal();
              }
            }
          "
          :isCustomerAdmin="props.isCustomerAdmin"
          :communityId="props.isCustomerAdmin ? globalRoot.orgInContext?.customerId : ''"
        />

        <GetStartedModal
          ref="domRefGetStartedModal"
          :isAuthenticated="authService.isAuthenticated"
          :creatorId="props.creatorId"
          :packageId="selectedPackageId"
          @existingCustomerClick="
            () => {
              domRefGetStartedModal?.closeModal();
              isNewDestinationOnboarding = false;
              isShowLoginModal = true;
            }
          "
          @newDestinationClick="handleNewDestinationClick"
        />

        <LoginModal
          v-if="isShowLoginModal"
          :isVisible="isShowLoginModal"
          @close="isShowLoginModal = false"
          :startWithNewUserFlow="isNewDestinationOnboarding"
          :redirectSettings="{ setRedirectPath: true, redirectUrlOverride: isNewDestinationOnboarding ? '/collab-onboarding' : null }"
        />
      </ul>

      <div v-if="props.isEditProfileMode" class="creator-packages-section__add-package-link-wrap" :title="packagesList.length >= 9 ? 'You have reached the package limit' : ''">
        <SrpButton
          class="creator-packages-section__add-package-link"
          @click="
            () => {
              if (packagesList.length < 9) {
                domRefEditPackageModal.openModal(props.creatorId);
              }
            }
          "
          size="small"
          fill="outlined"
          :isDisabled="packagesList.length >= 9"
        >
          <template #icon><IconEmbedded name="plus_3-5" :size="22" /></template>
          Add Package
        </SrpButton>
        <span class="global-h5" style="color: rgba(0, 0, 0, 0.5)">(optional)</span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { getInstance as getAuthWrapperInstance } from "@auth/authWrapper";
import CreatorPackagesRepo from "@repos/CreatorPackagesRepo";
import { CustomCollabOnboard } from "@logic/CustomCollabOnboard";

// Components
import GetStartedModal from "./GetStartedModal.vue";
import HowDoesItWorkModal from "./HowDoesItWorkModal.vue";
import PackageSnippet from "./PackageSnippet.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoginModal from "@components/Modals/LoginModal.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import EditPackageModal from "./EditPackageModal.vue";
import Loader from "@components/Loader/Loader.vue";

// Types
import { CreatorPackage } from "@contracts/creatorPackages";

// Global variables & injections
const authService = getAuthWrapperInstance();

// DOM refs
const domRefHowDoesItWorkModal = ref<any>(null);
const domRefEditPackageModal = ref<any>(null);
const domRefGetStartedModal = ref<any>(null);

const props = withDefaults(
  defineProps<{
    creatorId: string;
    isEditProfileMode: boolean;
    isCustomerAdmin: boolean;
  }>(),
  {
    creatorId: "",
    isEditProfileMode: false,
    isCustomerAdmin: false,
  }
);

const emit = defineEmits<{
  (e: "loadAllPackages", packagesList: Array<CreatorPackage>): void;
}>();

// Toggle login modal =========================================================
const isShowLoginModal = ref<boolean>(false);
const isNewDestinationOnboarding = ref<boolean>(false);
const selectedPackageId = ref<string>("");

function handleNewDestinationClick(creatorId: string, packageId: string) {
  // Save in localStorage using the new CustomCollabOnboard class
  CustomCollabOnboard.setCreatorPackageInContext(creatorId, packageId);

  // Close the get started modal and open login modal
  domRefGetStartedModal.value?.closeModal();
  isNewDestinationOnboarding.value = true;
  isShowLoginModal.value = true;
}

// Load creator packages ======================================================
const isLoadingPackages = ref<boolean>(true);
const packagesList = ref<Array<CreatorPackage>>([]);

watch(() => props.creatorId, loadAllPackages, { immediate: true });

async function loadAllPackages() {
  isLoadingPackages.value = true;
  packagesList.value = await CreatorPackagesRepo.loadAllPackages(props.creatorId);
  isLoadingPackages.value = false;

  emit("loadAllPackages", packagesList.value);
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Creator packages section ===================================================
.creator-packages-section {
  &__header {
    margin-bottom: 40px;
    display: flex;
    align-items: center;
  }

  &__title {
    margin-right: 23px;
  }

  &__how-does-it-work-link {
  }

  &__tiles-list {
    gap: $desktop-wide-grid-gap-width;
    padding: 0;
    margin: 0 0 25px;
    display: flex;
    flex-wrap: wrap;
  }

  &__tile {
    width: $desktop-wide-content-width-1-3rd;
  }

  &__add-package-link-wrap {
    margin-bottom: 40px;
    gap: 15px;
    display: flex;
    align-items: center;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__add-package-link {
  }

  &__explanation-info-section {
    margin: -15px 0 40px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .creator-packages-section {
    &__tiles-list {
      gap: $desktop-grid-gap-width;
    }

    &__tile {
      width: $desktop-content-width-1-3rd;
    }
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .creator-packages-section {
    &__tiles-list {
      gap: $laptop-grid-gap-width;
    }

    &__tile {
      width: $laptop-content-width-1-3rd;
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .creator-packages-section {
    &__tiles-list {
      gap: $tablet-large-grid-gap-width;
    }

    &__tile {
      width: $tablet-large-content-width-1-2nd;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .creator-packages-section {
    &__tiles-list {
      gap: $tablet-grid-gap-width;
    }

    &__tile {
      width: 100%;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .creator-packages-section {
    &__header {
      gap: 8px 0;
      flex-direction: column;
      align-items: flex-start;
    }

    &__tiles-list {
      gap: $mobile-grid-gap-width;
    }

    &__tile {
      width: 100%;
    }
  }
}
</style>
